import React, { useEffect, useMemo, useState } from "react";
import { Checkbox, FormControlLabel } from "@mui/material";
import { CustomDialog, CustomTextField, Selector } from "../../../../../../common/components";
import { translate } from "../../../../../../common/providers";
import {
	AI_PROVIDER_TYPES,
	getModelFormPayload,
	getProviderFieldConfig,
	translateEnumProvider,
	validateModelForm,
} from "../../utils";
import styles from "./ModelFormDialog.module.css";

// When editing an existing model, a blank secret field means "keep the stored secret".
// We feed this placeholder to the validator so those fields count as already provided
// instead of being reported as required.
const EXISTING_SECRET_PLACEHOLDER = "__existing_secret__";

const getModelDeploymentName = (currentModel) => currentModel?.deploymentName || currentModel?.deployment || "";

export default function ModelFormDialog({ model, open, onClose, onSubmit }) {
	const [name, setName] = useState("");
	const [description, setDescription] = useState("");
	const [providerType, setProviderType] = useState("AZURE_OPENAI");
	const [baseUrl, setBaseUrl] = useState("");
	const [deploymentName, setDeploymentName] = useState("");
	const [modelName, setModelName] = useState("");
	const [apiKey, setApiKey] = useState("");
	const [region, setRegion] = useState("");
	const [projectId, setProjectId] = useState("");
	const [location, setLocation] = useState("");
	const [accessKey, setAccessKey] = useState("");
	const [secretKey, setSecretKey] = useState("");
	const [providerOptions, setProviderOptions] = useState("");
	const [inputCostPerMillion, setInputCostPerMillion] = useState("");
	const [outputCostPerMillion, setOutputCostPerMillion] = useState("");
	const [errors, setErrors] = useState({});
	const [active, setActive] = useState(true);

	useEffect(() => {
		if (model) {
			setName(model.name || "");
			setDescription(model.description || "");
			setProviderType(model.providerType || "AZURE_OPENAI");
			setBaseUrl(model.baseUrl || "");
			setDeploymentName(getModelDeploymentName(model));
			setModelName(model.modelName || "");
			setApiKey("");
			setRegion(model.region || "");
			setProjectId(model.projectId || "");
			setLocation(model.location || "");
			setAccessKey("");
			setSecretKey("");
			setProviderOptions(model.providerOptions || "");
			setInputCostPerMillion(`${model.inputCostPerMillion ?? ""}`);
			setOutputCostPerMillion(`${model.outputCostPerMillion ?? ""}`);
			setErrors({});
			setActive(model.active !== false);
		} else {
			setName("");
			setDescription("");
			setProviderType("AZURE_OPENAI");
			setBaseUrl("");
			setDeploymentName("");
			setModelName("");
			setApiKey("");
			setRegion("");
			setProjectId("");
			setLocation("");
			setAccessKey("");
			setSecretKey("");
			setProviderOptions("");
			setInputCostPerMillion("");
			setOutputCostPerMillion("");
			setErrors({});
			setActive(true);
		}
	}, [model, open]);

	const providerFieldConfig = useMemo(() => getProviderFieldConfig(providerType), [providerType]);
	const shouldRenderDeploymentName = providerFieldConfig.deploymentName || !!getModelDeploymentName(model);
	const validateCost = (value) => {
		if (`${value}`.trim().length === 0) {
			return translate("dsi.agents.ai-models.validation.required");
		}
		if (Number.isNaN(Number(value))) {
			return translate("dsi.agents.ai-models.validation.numeric");
		}
		if (Number(value) < 0) {
			return translate("dsi.agents.ai-models.validation.non-negative");
		}
		return "";
	};
	// A blank secret while editing stands in for the stored secret; both the live
	// validity check and submit use this so the button state matches the submit result.
	const keepExistingSecret = (value) => (model && !value ? EXISTING_SECRET_PLACEHOLDER : value);
	const buildProviderErrors = () =>
		validateModelForm({
			providerType,
			baseUrl,
			apiKey: keepExistingSecret(apiKey),
			deploymentName,
			modelName,
			region,
			projectId,
			location,
			accessKey: keepExistingSecret(accessKey),
			secretKey: keepExistingSecret(secretKey),
		});
	const inputCostError = validateCost(inputCostPerMillion);
	const outputCostError = validateCost(outputCostPerMillion);
	const isValid =
		name.trim().length > 0 &&
		!inputCostError &&
		!outputCostError &&
		Object.keys(buildProviderErrors()).length === 0;

	const handleSubmit = () => {
		const nextErrors = {
			...buildProviderErrors(),
			inputCostPerMillion: validateCost(inputCostPerMillion),
			outputCostPerMillion: validateCost(outputCostPerMillion),
		};
		setErrors(nextErrors);
		if (Object.values(nextErrors).some(Boolean)) {
			return;
		}
		onSubmit(
			getModelFormPayload({
				name: name.trim(),
				description: description.trim(),
				providerType,
				baseUrl: baseUrl.trim(),
				deploymentName: deploymentName.trim(),
				modelName: modelName.trim(),
				apiKey: apiKey.trim(),
				region: region.trim(),
				projectId: projectId.trim(),
				location: location.trim(),
				accessKey: accessKey.trim(),
				secretKey: secretKey.trim(),
				providerOptions: providerOptions.trim(),
				active,
				inputCostPerMillion,
				outputCostPerMillion,
			})
		);
	};

	const renderTextField = (field, label, value, setValue, type = "text", helperText = undefined) => (
		<CustomTextField
			fullWidth
			error={!!errors[field]}
			helperText={errors[field] || helperText}
			label={label}
			type={type}
			value={value}
			onChange={(e) => {
				setValue(e.target.value);
				setErrors((prev) => ({ ...prev, [field]: "" }));
			}}
		/>
	);

	return (
		<CustomDialog
			displayCloseCross
			isForm
			open={open}
			submitDisabled={!isValid}
			submitLabel={translate("common:btn.validate")}
			title={translate(model ? "dsi.agents.ai-models.dialog.edit" : "dsi.agents.ai-models.dialog.create")}
			onClose={onClose}
			onSubmit={handleSubmit}
		>
			<div className={styles.dialogContent}>
				<CustomTextField
					fullWidth
					label={translate("common:field.name")}
					value={name}
					onChange={(e) => setName(e.target.value)}
				/>
				<CustomTextField
					fullWidth
					multiline
					label={translate("common:field.description")}
					minRows={2}
					value={description}
					onChange={(e) => setDescription(e.target.value)}
				/>
				<Selector
					fullWidth
					itemRenderer={(value) => translateEnumProvider(value)}
					items={AI_PROVIDER_TYPES}
					label={translate("dsi.agents.models.table.provider")}
					value={providerType}
					valueRenderer={(value) => translateEnumProvider(value)}
					onChange={(e) => {
						setProviderType(e.target.value);
						setErrors({});
					}}
				/>
				{providerFieldConfig.baseUrl &&
					renderTextField("baseUrl", translate("dsi.agents.ai-models.base-url"), baseUrl, setBaseUrl)}
				{shouldRenderDeploymentName &&
					renderTextField(
						"deploymentName",
						translate("dsi.agents.models.table.deployment"),
						deploymentName,
						setDeploymentName
					)}
				{providerFieldConfig.modelName &&
					renderTextField("modelName", translate("dsi.agents.models.table.model"), modelName, setModelName)}
				{providerFieldConfig.apiKey &&
					renderTextField(
						"apiKey",
						translate("dsi.agents.ai-models.api-key"),
						apiKey,
						setApiKey,
						"password",
						model?.hasApiKey ? translate("dsi.agents.ai-models.secret-optional") : undefined
					)}
				{providerFieldConfig.region &&
					renderTextField("region", translate("dsi.agents.ai-models.region"), region, setRegion)}
				{providerFieldConfig.projectId &&
					renderTextField("projectId", translate("dsi.agents.ai-models.project-id"), projectId, setProjectId)}
				{providerFieldConfig.location &&
					renderTextField("location", translate("dsi.agents.ai-models.location"), location, setLocation)}
				{providerFieldConfig.accessKey &&
					renderTextField(
						"accessKey",
						translate("dsi.agents.ai-models.access-key"),
						accessKey,
						setAccessKey,
						"password",
						model?.hasSecretKey ? translate("dsi.agents.ai-models.secret-optional") : undefined
					)}
				{providerFieldConfig.secretKey &&
					renderTextField(
						"secretKey",
						translate("dsi.agents.ai-models.secret-key"),
						secretKey,
						setSecretKey,
						"password",
						model?.hasSecretKey ? translate("dsi.agents.ai-models.secret-optional") : undefined
					)}
				<CustomTextField
					fullWidth
					multiline
					label={translate("dsi.agents.ai-models.provider-options")}
					minRows={2}
					value={providerOptions}
					onChange={(e) => setProviderOptions(e.target.value)}
				/>
				<CustomTextField
					fullWidth
					error={!!errors.inputCostPerMillion}
					helperText={errors.inputCostPerMillion || translate("dsi.agents.ai-models.input-cost.helper")}
					inputProps={{ min: 0, step: "any" }}
					label={translate("dsi.agents.ai-models.input-cost.label")}
					type="number"
					value={inputCostPerMillion}
					onChange={(e) => {
						setInputCostPerMillion(e.target.value);
						setErrors((prev) => ({ ...prev, inputCostPerMillion: "" }));
					}}
				/>
				<CustomTextField
					fullWidth
					error={!!errors.outputCostPerMillion}
					helperText={errors.outputCostPerMillion || translate("dsi.agents.ai-models.output-cost.helper")}
					inputProps={{ min: 0, step: "any" }}
					label={translate("dsi.agents.ai-models.output-cost.label")}
					type="number"
					value={outputCostPerMillion}
					onChange={(e) => {
						setOutputCostPerMillion(e.target.value);
						setErrors((prev) => ({ ...prev, outputCostPerMillion: "" }));
					}}
				/>
				<FormControlLabel
					control={<Checkbox checked={active} onChange={(e) => setActive(e.target.checked)} />}
					label={translate("dsi.agents.models.status.active")}
				/>
			</div>
		</CustomDialog>
	);
}
